import React, { FC } from 'react';
import { Badge, Button, TextLink } from '@kontentino/ui';
import { PostDetail } from 'types/PostDetail';
import PostPreview, {
  PostPreviewVariant,
} from '@kontentino/kontentino-post-previews';
import clsx from 'clsx';
import PostPreviewContentBox from 'app/components/postPreviewWithVariants/PostPreviewContentBox';
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import { faText } from '@fortawesome/pro-regular-svg-icons/faText';
import { SUBTYPE } from '@kontentino/kontentino-constants/Posts';
import { PageNumberType, TYPE } from '@kontentino/kontentino-constants/Pages';
import { useUser } from 'modules/user/userSelector';
import { useTranslation } from 'react-i18next';
import RouterLink from 'components/shared/Link';
import { modalRoutes } from 'constants/routes';
import { createSearchFromParams } from 'utils/url';
import { coerceToPreviewAttachments } from 'utils/previewCoercion';

type Props = {
  variant: PostPreviewVariant;
  revision: {
    data: PostDetail;
    label: string;
    id?: number;
    title: string;
  };
};

const PostRevisionsPreview: FC<Props> = ({ variant, revision }) => {
  const user = useUser();
  const { t } = useTranslation();

  return (
    <>
      <div className="tw-max-w-[320px]">
        <div className="tw-my-3 tw-flex tw-w-[320px] tw-items-center tw-space-x-2">
          <Badge variant="gray">{revision.label}</Badge>
          <p className="tw-text-sm tw-font-medium">{revision.title}</p>
          {revision.id && (
            <RouterLink
              data-name="posts-list-day-post-more-edit"
              to={{
                hash: modalRoutes.POST_REVISION_DETAIL,
                search: createSearchFromParams({
                  id: revision.data.id,
                  revisionId: revision.id,
                  compose: true,
                }),
              }}
            >
              <TextLink>
                <Button
                  variant="plain"
                  size="small"
                  className="tw-text-md tw-font-semibold"
                >
                  {t('open')}
                </Button>
              </TextLink>
            </RouterLink>
          )}
        </div>
        {revision.data && (
          <>
            <div
              className={clsx({
                'tw-rounded-md tw-border  tw-border-grayscale-20':
                  variant === 'desktop',
              })}
            >
              <PostPreview
                content={{
                  ...revision.data,
                  page: {
                    id: revision.data.page.id,
                    name: revision.data.page.name,
                    type: revision.data.page.type as PageNumberType,
                    logo_src: revision.data.page.logo?.src ?? null,
                  },
                  attachments: coerceToPreviewAttachments(
                    revision.data.attachments,
                  ),
                }}
                variant={variant}
                use12hourFormat={!user.useMeridianTime}
              />
            </div>
            <div className="tw-mt-3">
              {!!revision.data.text &&
                revision.data.page.type === TYPE.INSTAGRAM &&
                revision.data.subtype === SUBTYPE.INSTAGRAM_STORY && (
                  <PostPreviewContentBox
                    title={t('postText')}
                    content={revision.data.text}
                    icon={faText}
                    data-cy="post-preview-additional-content_post-text"
                  />
                )}

              {!!revision.data.attachments.firstComment?.text?.length && (
                <PostPreviewContentBox
                  title={t('firstComment.title')}
                  content={revision.data.attachments.firstComment.text}
                  icon={faComment}
                  textClassName="tw-whitespace-break-spaces"
                  data-cy="post-preview-additional-content_post-first-comment"
                />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default PostRevisionsPreview;
